// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React from "react";
import { useLogger } from "amazon-chime-sdk-component-library-react";
import { useDevices } from "../../../providers/DevicesProvider";
import DeviceInput from "../DeviceInput";
import { useBrowserCompatibilityFeatures } from "@platform/features/video/hooks/useBrowserCompatibilityFeatures";

interface Props {
  /** The message that will be shown when no camera devices are found. */
  notFoundMsg?: string;
  /** The label that will be shown for camera selection, it defaults to "Camera source". */
  label?: string;
}

export const CameraSelection: React.FC<React.PropsWithChildren<Props>> = ({
  notFoundMsg = "No camera devices found",
  label = "Camera source",
  ...rest
}) => {
  const logger = useLogger();
  const {
    videoInputDevices,
    selectedVideoInputDevice,
    selectVideoInputDevice,
  } = useDevices();

  const { supportsVideo } = useBrowserCompatibilityFeatures();

  const handleSelect = async (deviceId: string): Promise<void> => {
    try {
      await selectVideoInputDevice(deviceId);
      console.log('Selected video input device on join page:', deviceId);
    } catch (error) {
      logger.error("CameraSelection failed to select camera");
    }
  };

  return (
    <DeviceInput
      name="camera-selection"
      label={label}
      onChange={handleSelect}
      devices={videoInputDevices}
      selectedDevice={selectedVideoInputDevice}
      notFoundMsg={notFoundMsg}
      disabled={!supportsVideo}
      {...rest}
    />
  );
};

export default CameraSelection;
